import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Home,
  Settings,
  X,
  FolderOpen,
  ChevronDown,
} from "lucide-react";
import { useUser } from "../contexts/UserContext";
import { useProject } from "../contexts/ProjectContext";
import SettingsModal from "./SettingsModal";

const Sidebar = ({
  isOpen,
  isCollapsed,
  onClose,
  onToggleCollapse,
  onMenuClick,
  sidebarOpen,
}) => {
  const location = useLocation();
  const { user } = useUser();
  const { projects } = useProject();

  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const navRef = useRef(null);

  const activeProjectId = useMemo(() => {
    const match = location.pathname.match(/\/project\/([^/]+)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  // Group projects by category (alphabetically sorted)
  const groupedProjects = useMemo(() => {
    if (!projects || projects.length === 0) return { categories: [], uncategorized: [], workspaceCategory: null };

    const categoryMap = new Map();
    const workspace = { name: "Workspace", projects: [], color: "#6366f1" };

    projects.forEach((project) => {
      if (project.category && project.category._id) {
        const catId = project.category._id;
        // If category is Workspace, add to workspace group
        if (project.category.name === "Workspace") {
          workspace.projects.push(project);
          if (!workspace._id) workspace._id = catId;
          if (!workspace.color) workspace.color = project.category.color;
        } else {
          if (!categoryMap.has(catId)) {
            categoryMap.set(catId, {
              ...project.category,
              projects: [],
            });
          }
          categoryMap.get(catId).projects.push(project);
        }
      } else {
        workspace.projects.push(project);
      }
    });

    // Sort categories alphabetically by name
    const categories = Array.from(categoryMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    // Sort projects within each category alphabetically
    categories.forEach((cat) => {
      cat.projects.sort((a, b) => a.name.localeCompare(b.name));
    });

    // Sort workspace projects alphabetically
    if (workspace.projects.length > 0) {
      workspace.projects.sort((a, b) => a.name.localeCompare(b.name));
    }

    return { categories, uncategorized: [], workspaceCategory: workspace.projects.length > 0 ? workspace : null };
  }, [projects]);

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // Initialize all categories and projects menu as expanded
  useEffect(() => {
    const initialExpanded = { projects: true };
    groupedProjects.categories.forEach((cat) => {
      initialExpanded[cat._id] = true;
    });
    if (groupedProjects.workspaceCategory?._id) {
      initialExpanded[groupedProjects.workspaceCategory._id] = true;
    }
    setExpandedCategories((prev) => ({ ...initialExpanded, ...prev }));
  }, [
    groupedProjects.categories.length,
    groupedProjects.workspaceCategory?._id,
  ]);

  // Keep the active project's category open so the link is visible.
  useEffect(() => {
    if (!activeProjectId) return;

    const findCategoryIdForProject = () => {
      const directCategory = groupedProjects.categories.find((cat) =>
        cat.projects.some((p) => p._id === activeProjectId)
      );
      if (directCategory) return directCategory._id;

      if (
        groupedProjects.workspaceCategory?.projects.some(
          (p) => p._id === activeProjectId
        )
      ) {
        return groupedProjects.workspaceCategory._id;
      }
      return null;
    };

    const categoryId = findCategoryIdForProject();
    if (categoryId) {
      setExpandedCategories((prev) => ({
        ...prev,
        projects: true,
        [categoryId]: true,
      }));
    } else {
      setExpandedCategories((prev) => ({ ...prev, projects: true }));
    }
  }, [activeProjectId, groupedProjects]);

  useEffect(() => {
    if (isCollapsed) {
      setShowSettingsModal(false);
    }
  }, [isCollapsed]);

  // Keep the active link reachable without forcing the sidebar away from
  // category headers that are already visible.
  useEffect(() => {
    if (!activeProjectId || !navRef.current) return;
    const activeElement = navRef.current.querySelector(
      `[data-project-id=\"${activeProjectId}\"]`
    );
    if (!activeElement?.scrollIntoView) return;

    const navRect = navRef.current.getBoundingClientRect();
    const activeRect = activeElement.getBoundingClientRect();
    const isAbove = activeRect.top < navRect.top;
    const isBelow = activeRect.bottom > navRect.bottom;

    if (isAbove || isBelow) {
      activeElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [activeProjectId, groupedProjects, isCollapsed]);

  const isOnProjectPage = location.pathname.includes("/project/");

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-40 bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700 transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col h-full ${isCollapsed ? "w-16" : "w-72"
          } ${isOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        {/* Mobile-only close bar; the collapse toggle and brand now live in the top Header, which stays fixed regardless of sidebar state */}
        <div className="flex items-center justify-end px-4 py-3 border-b border-gray-200 dark:border-slate-700 lg:hidden">
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200"
          >
            <X className="w-4 h-4 text-gray-600 dark:text-slate-400" />
          </button>
        </div>

        <nav
          ref={navRef}
          className={`flex-1 ${isCollapsed ? "px-2 py-2" : "px-3 py-2"} space-y-1 overflow-y-auto custom-scrollbar transition-colors duration-200`}
        >
          {/* Dashboard */}
          <Link
            to="/dashboard"
            onClick={onClose}
            className={`flex items-center ${isCollapsed
              ? "justify-center w-full px-1 py-2"
              : "justify-between px-[8px] py-[6px]"
              } rounded-lg transition-all duration-200 ${!isOnProjectPage && location.pathname === "/dashboard"
                ? "bg-blue-100 dark:bg-blue-900/30 text-[#292A2E] dark:text-blue-400 border border-blue-200 dark:border-blue-800"
                : "text-[#292A2E] dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-900 dark:hover:text-white"
              }`}
            title={isCollapsed ? "Dashboard" : ""}
          >
            <div className={`flex items-center ${isCollapsed ? "justify-center" : "space-x-3"}`}>
              <Home className="w-4 h-4" />
              {!isCollapsed && <span className="font-medium">Dashboard</span>}
            </div>
          </Link>


          {/* Main Projects Menu */}
          {["admin", "member"].includes(user?.role) && !isCollapsed && (
            <div>
              {/* Projects Header */}
              <button
                onClick={() => toggleCategory("projects")}
                className={`w-full flex items-center justify-between px-[8px] py-[6px] rounded-lg transition-all duration-200 ${isOnProjectPage
                  ? "bg-blue-100 dark:bg-blue-900/30 text-[#292A2E] dark:text-blue-400 border border-blue-200 dark:border-blue-800"
                  : "text-[#292A2E] dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-900 dark:hover:text-white"
                  }`}
              >
                <div className="flex items-center space-x-3 min-w-0">
                  <FolderOpen className="w-4 h-4" />
                  <span className="font-medium">Projects</span>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${expandedCategories["projects"] ? "rotate-180" : ""
                    }`}
                />
              </button>

              {/* Projects Content */}
              <div
                className={`transition-all duration-200 ${expandedCategories["projects"]
                  ? "max-h-none opacity-100 overflow-visible"
                  : "max-h-0 opacity-0 overflow-hidden"
                  }`}
              >
                <div className="mt-1 space-y-0.5 ml-4 pl-3 pb-3 border-l border-gray-100 dark:border-slate-700">
                  {/* Categories - each as expandable section */}
                  {groupedProjects.categories.map((category) => (
                    <div key={category._id}>
                      {/* Category Header */}
                      <button
                        onClick={() => toggleCategory(category._id)}
                        className={`w-full flex items-center justify-between px-[8px] py-[6px] rounded-lg transition-all duration-200 ${category.projects.some(
                          (p) => location.pathname === `/project/${p._id}`
                        )
                          ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                          : "text-[#292A2E] dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-900 dark:hover:text-white"
                          }`}
                      >
                        <div className="flex items-center space-x-2 min-w-0">
                          <div
                            className="w-4 h-4 rounded flex-shrink-0 flex items-center justify-center"
                            style={{ backgroundColor: category.color || "#6366f1" }}
                          >
                            <FolderOpen className="w-2.5 h-2.5 text-white" />
                          </div>
                          <span className="text-sm font-medium truncate">
                            {category.name}
                          </span>
                        </div>
                        <ChevronDown
                          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${expandedCategories[category._id] ? "rotate-180" : ""
                            }`}
                        />
                      </button>

                      {/* Category Projects */}
                      <div
                        className={`transition-all duration-200 ${expandedCategories[category._id]
                          ? "max-h-none opacity-100 overflow-visible"
                          : "max-h-0 opacity-0 overflow-hidden"
                          }`}
                      >
                        <div className="mt-0.5 space-y-0.5 ml-3 pl-2 border-l border-gray-100 dark:border-slate-700">
                          {category.projects.map((project) => {
                            const isProjectActive =
                              location.pathname === `/project/${project._id}`;
                            return (
                              <Link
                                key={project._id}
                                to={`/project/${project._id}`}
                                onClick={onClose}
                                data-project-id={project._id}
                                className={`flex items-center gap-2 px-2 py-1.5 rounded-md transition-all duration-200 ${isProjectActive
                                  ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium"
                                  : "text-[#292A2E] dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-600 hover:text-gray-700 dark:hover:text-white"
                                  }`}
                              >
                                <span className="text-sm truncate">{project.name}</span>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Uncategorized Projects - displayed directly inside Projects */}
                  {groupedProjects.uncategorized.map((project) => {
                    const isProjectActive =
                      location.pathname === `/project/${project._id}`;
                    return (
                      <Link
                        key={project._id}
                        to={`/project/${project._id}`}
                        onClick={onClose}
                        data-project-id={project._id}
                        className={`flex items-center px-2 py-2 rounded-md transition-colors duration-200 ${isProjectActive
                          ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium"
                          : "text-[#292A2E] dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-900 dark:hover:text-white"
                          }`}
                      >
                        <div className="flex items-center space-x-2 min-w-0">
                          <div className="w-4 h-4 rounded flex-shrink-0 flex items-center justify-center bg-gray-400">
                            <FolderOpen className="w-2.5 h-2.5 text-white" />
                          </div>
                          <span className="text-sm truncate">{project.name}</span>
                        </div>
                      </Link>
                    );
                  })}

                  {/* Workspace Category - displayed at the bottom */}
                  {groupedProjects.workspaceCategory && (
                    <div key={groupedProjects.workspaceCategory._id}>
                      {/* Category Header */}
                      <button
                        onClick={() => toggleCategory(groupedProjects.workspaceCategory._id)}
                        className={`w-full flex items-center justify-between px-2 py-2 rounded-md transition-all duration-200 ${groupedProjects.workspaceCategory.projects.some(
                          (p) => location.pathname === `/project/${p._id}`
                        )
                          ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                          : "text-[#292A2E] dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-900 dark:hover:text-white"
                          }`}
                      >
                        <div className="flex items-center space-x-2 min-w-0">
                          <div
                            className="w-4 h-4 rounded flex-shrink-0 flex items-center justify-center"
                            style={{ backgroundColor: groupedProjects.workspaceCategory.color || "#6366f1" }}
                          >
                            <FolderOpen className="w-2.5 h-2.5 text-white" />
                          </div>
                          <span className="text-sm font-medium truncate">
                            {groupedProjects.workspaceCategory.name}
                          </span>
                        </div>
                        <ChevronDown
                          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${expandedCategories[groupedProjects.workspaceCategory._id] ? "rotate-180" : ""
                            }`}
                        />
                      </button>

                      {/* Category Projects */}
                      <div
                        className={`transition-all duration-200 ${expandedCategories[groupedProjects.workspaceCategory._id]
                          ? "max-h-none opacity-100 overflow-visible"
                          : "max-h-0 opacity-0 overflow-hidden"
                          }`}
                      >
                        <div className="mt-1 space-y-0.5 ml-3 pl-2 border-l border-gray-200 dark:border-slate-700">
                          {groupedProjects.workspaceCategory.projects.map((project) => {
                            const isProjectActive =
                              location.pathname === `/project/${project._id}`;
                            return (
                              <Link
                                key={project._id}
                                to={`/project/${project._id}`}
                                onClick={onClose}
                                data-project-id={project._id}
                                className={`flex items-center gap-2 px-2 py-1.5 rounded-md transition-all duration-200 ${isProjectActive
                                  ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium"
                                  : "text-[#292A2E] dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-600 hover:text-gray-700 dark:hover:text-white"
                                  }`}
                              >
                                <span className="text-sm truncate z-50">{project.name}</span>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Collapsed state - show Projects icon */}
          {["admin", "member"].includes(user?.role) && isCollapsed && (
            <div
              className={`flex items-center justify-center px-1 py-2 rounded-lg transition-all duration-200 cursor-pointer ${isOnProjectPage
                ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800"
                : "text-[#292A2E] dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-900 dark:hover:text-white"
                }`}
              title="Projects"
              onClick={onToggleCollapse}
            >
              <FolderOpen className="w-4 h-4" />
            </div>
          )}
        </nav>

        {/* User info */}
        <div className="relative border-t border-gray-200 dark:border-slate-700 mt-auto transition-colors duration-200">
          <div
            className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700 transition-all duration-200 ${isCollapsed ? "p-3" : "p-3"}`}
            onClick={() => setShowSettingsModal(true)}
          >
            <div
              className={`flex items-center ${isCollapsed ? "justify-center" : "space-x-3"
                }`}
            >
              <Settings className="w-4 h-4 text-gray-600 dark:text-slate-400" />
              {!isCollapsed && (
                <p className="text-sm font-medium text-gray-900 dark:text-white">Settings</p>
              )}
            </div>
          </div>

          {/* Settings Modal */}
          <SettingsModal
            isOpen={showSettingsModal}
            onClose={() => setShowSettingsModal(false)}
          />
        </div>
      </div>
    </>
  );
};

export default Sidebar;

